
      import '@folkjs/labs/standalone/folk-shape';
      import { FolkRTC } from '@folkjs/labs/FolkRTC';

      // DOM Elements
      const connectionStateIndicator = document.getElementById('connectionStateIndicator');
      const createRoomBtn = document.getElementById('createRoomBtn');
      const shareUrlContainer = document.getElementById('shareUrlContainer');
      const shareUrlInput = document.getElementById('shareUrlInput');
      const connectionLog = document.getElementById('connectionLog');
      const chatContainer = document.getElementById('chatContainer');
      const testMessageInput = document.getElementById('testMessageInput');
      const sendTestBtn = document.getElementById('sendTestBtn');

      // Core state
      let gun = null;
      let room = null;
      let rtcConnections = {};
      let clientId = generateClientId();
      let currentRoomId = null;

      // Track processed data to avoid duplicates
      let processedOffers = new Set();
      let processedAnswers = new Set();
      let knownPeers = new Set();

      // Debug settings
      const DEBUG = true;

      // Connection states
      const CONNECTION_STATES = {
        WAITING: 'waiting',
        CONNECTING: 'connecting',
        CONNECTED: 'connected',
        FAILED: 'failed',
      };

      // Update connection state
      function updateConnectionState(state) {
        connectionStateIndicator.textContent = state;
        connectionStateIndicator.className = `connection-state state-${state}`;
      }

      // Log function
      function log(message, type = 'system') {
        const entry = document.createElement('div');
        entry.className = `log-entry ${type}`;
        entry.textContent = message;
        connectionLog.appendChild(entry);
        connectionLog.scrollTop = connectionLog.scrollHeight;
        console.log(`[${type}] ${message}`);
      }

      // Debug log - only shows if DEBUG is true
      function debugLog(message, type = 'debug') {
        if (DEBUG) {
          console.log(`[${type}] ${message}`);
        }
      }

      // Generate a unique client ID
      function generateClientId() {
        return Math.random().toString(36).substring(2, 7);
      }

      // Generate a unique room ID
      function generateRoomId() {
        return Math.random().toString(36).substring(2, 7);
      }

      // Determine initiator based on client IDs - using string comparison
      function determineInitiator(id1, id2) {
        return id1.localeCompare(id2) > 0 ? id1 : id2;
      }

      // Update connection indicators
      function updateConnectionStatus() {
        // Debug output to help diagnose connection issues
        for (const peerId in rtcConnections) {
          const rtc = rtcConnections[peerId];
          debugLog(
            `Connection state for peer ${peerId}: ${rtc.isConnected() ? 'connected' : 'not connected'}`,
            'debug',
          );
        }

        // Update WebRTC indicator
        const hasActiveRTC = Object.values(rtcConnections).some((conn) => conn.isConnected());
        debugLog(`Has any active RTC connection: ${hasActiveRTC}`, 'debug');

        if (hasActiveRTC) {
          updateConnectionState(CONNECTION_STATES.CONNECTED);
          sendTestBtn.disabled = false;
        } else if (Object.keys(rtcConnections).length > 0) {
          // If we have connection attempts but none are connected
          updateConnectionState(CONNECTION_STATES.CONNECTING);
          sendTestBtn.disabled = true;
        } else if (currentRoomId) {
          // We're in a room but no connections
          updateConnectionState(CONNECTION_STATES.WAITING);
          sendTestBtn.disabled = true;
        } else {
          // Not even in a room
          updateConnectionState(CONNECTION_STATES.WAITING);
          sendTestBtn.disabled = true;
        }
      }

      // Create a new FolkRTC connection for a peer
      function createRTCConnection(peerId) {
        if (rtcConnections[peerId]) {
          return rtcConnections[peerId];
        }

        log(`Creating RTC connection for peer ${peerId}`, 'rtc');

        // Create new connection with STUN servers
        const rtc = new FolkRTC();

        rtc.onStatusChange = (status) => {
          log(`RTC status with ${peerId}: ${status}`, 'rtc');

          // Delay updating the status slightly to allow connection to fully establish
          setTimeout(() => {
            updateConnectionStatus();

            // Try sending a test message if we're connected
            if (status === 'connected' && rtc.isConnected()) {
              log(`Testing connection with ${peerId}...`, 'rtc');
              rtc.sendMessage('connection-test');
            }
          }, 500);
        };

        rtc.onMessage = (message) => {
          log(`Received RTC message from ${peerId}: ${message}`, 'rtc');

          // Connection received a message, make sure we update status
          updateConnectionStatus();

          // Don't display test messages in the UI
          if (message === 'connection-test') return;

          // Add to chat interface
          addChatMessage(message, peerId);
        };

        rtcConnections[peerId] = rtc;
        return rtc;
      }

      // Add message to chat container
      function addChatMessage(message, senderId) {
        const entry = document.createElement('div');

        if (senderId === 'self') {
          entry.className = 'chat-message sent';
          entry.textContent = message;
        } else {
          entry.className = 'chat-message received';
          entry.textContent = message;
        }

        chatContainer.appendChild(entry);
        chatContainer.scrollTop = chatContainer.scrollHeight;
      }

      // Connect to GunDB room
      function connectToRoom(roomId) {
        try {
          // Initialize Gun with public relay
          gun = Gun({
            peers: ['https://gun-manhattan.herokuapp.com/gun'],
            localStorage: false, // Don't persist data locally
            radisk: false, // Disable radisk
          });

          currentRoomId = roomId;

          // Get the room node directly - prefix to avoid conflicts
          room = gun.get('folksync-' + roomId);

          log(`Connected to room: ${roomId}`, 'gun');

          // Display client ID
          log(`Your client ID: ${clientId}`, 'system');

          // Update connection indicator
          updateConnectionStatus();

          // Set our ready state with a unique key to ensure it persists
          const readyData = {
            time: Date.now(),
            id: clientId, // Include ID in the data for easier debugging
          };
          room.get('ready').get(clientId).put(readyData);
          log(`Set ready state in room`, 'gun');

          // Listen for other clients' ready states
          room
            .get('ready')
            .map()
            .on((data, peerId) => {
              // Skip our own ready state or invalid data
              if (peerId === clientId || !data || !data.time) return;

              // Skip already processed peers
              if (knownPeers.has(peerId)) return;

              knownPeers.add(peerId);
              log(`Detected peer: ${peerId}`, 'gun');
              debugLog(`Peer data: ${JSON.stringify(data)}`);

              // Handle peer discovery
              handlePeerDiscovery(peerId);
            });

          // Listen for offers directed to us - use once() to prevent duplicate processing
          room
            .get('offer')
            .get(clientId)
            .on((offerData) => {
              if (!offerData || !offerData.from || !offerData.sdp) return;

              // Create a unique key for this offer to detect duplicates
              const offerKey = `${offerData.from}_${offerData.time || Date.now()}`;

              // Skip if we've already processed this offer
              if (processedOffers.has(offerKey)) {
                debugLog(`Skipping duplicate offer from ${offerData.from}`, 'gun');
                return;
              }

              processedOffers.add(offerKey);
              log(`Received WebRTC offer from ${offerData.from}`, 'rtc');
              debugLog(`Offer data: ${JSON.stringify(offerData)}`);

              // Process the offer
              handleIncomingOffer(offerData);
            });

          // Listen for answers to our offers
          room
            .get('answer')
            .get(clientId)
            .on((answerData) => {
              if (!answerData || !answerData.from || !answerData.sdp) return;

              // Create a unique key for this answer to detect duplicates
              const answerKey = `${answerData.from}_${answerData.time || Date.now()}`;

              // Skip if we've already processed this answer
              if (processedAnswers.has(answerKey)) {
                debugLog(`Skipping duplicate answer from ${answerData.from}`, 'gun');
                return;
              }

              processedAnswers.add(answerKey);
              log(`Received WebRTC answer from ${answerData.from}`, 'rtc');
              debugLog(`Answer data: ${JSON.stringify(answerData)}`);

              // Process the answer
              handleIncomingAnswer(answerData);
            });

          return true;
        } catch (error) {
          log(`Error connecting to room: ${error.message}`, 'error');
          console.error('Full error:', error);
          updateConnectionState(CONNECTION_STATES.FAILED);
          return false;
        }
      }

      // Handle newly discovered peer
      function handlePeerDiscovery(peerId) {
        // Skip if already connected or connecting
        if (rtcConnections[peerId] && rtcConnections[peerId].isConnected()) {
          debugLog(`Already connected to ${peerId}, skipping discovery`, 'rtc');
          return;
        }

        log(`Handling peer discovery for ${peerId}`, 'rtc');
        updateConnectionState(CONNECTION_STATES.CONNECTING);

        // Simple role determination - whoever has the "larger" ID initiates
        const initiatorId = determineInitiator(clientId, peerId);
        log(`Initiator determined to be: ${initiatorId}`, 'rtc');

        if (initiatorId === clientId) {
          log(`We are the initiator for peer ${peerId}`, 'rtc');

          // Create an offer after a small delay to allow both sides to set up
          setTimeout(() => {
            initiateRTCConnection(peerId);
          }, 500);
        } else {
          log(`We are the receiver for peer ${peerId}, waiting for offer`, 'rtc');
          // Just create the connection object, wait for offer
          createRTCConnection(peerId);
        }
      }

      // Initiate RTC connection by creating and sending an offer
      async function initiateRTCConnection(peerId) {
        // Skip if we're already connected
        if (rtcConnections[peerId] && rtcConnections[peerId].isConnected()) {
          debugLog(`Already connected to ${peerId}, skipping initiation`, 'rtc');
          return;
        }

        // Create RTC connection if it doesn't exist
        const rtc = createRTCConnection(peerId);

        try {
          // Create offer - this automatically creates the data channel
          log(`Creating WebRTC offer for peer ${peerId}...`, 'rtc');
          const offer = await rtc.createOffer();
          log(`Created offer for ${peerId}`, 'rtc');

          // Store the offer directly on the target peer's node
          const offerData = {
            from: clientId,
            sdp: offer,
            time: Date.now(),
          };

          debugLog(`Storing offer: ${JSON.stringify(offerData)}`, 'rtc');

          // Use put with a callback to ensure the offer is stored
          room
            .get('offer')
            .get(peerId)
            .put(offerData, (ack) => {
              if (ack.err) {
                log(`Error storing offer: ${ack.err}`, 'error');
              } else {
                log(`Stored offer for ${peerId} in GunDB`, 'gun');
              }
            });
        } catch (error) {
          log(`Error creating offer: ${error.message}`, 'error');
          console.error('Full error:', error);
          updateConnectionState(CONNECTION_STATES.FAILED);
        }
      }

      // Handle incoming WebRTC offer
      async function handleIncomingOffer(offerData) {
        const senderId = offerData.from;

        log(`Processing offer from ${senderId}`, 'rtc');

        // Create RTC connection if it doesn't exist
        const rtc = createRTCConnection(senderId);

        try {
          // Create answer from offer - this setup is handled by FolkRTC
          log(`Creating answer for ${senderId}...`, 'rtc');
          const answer = await rtc.createAnswer(offerData.sdp);
          log(`Created answer for ${senderId}`, 'rtc');

          // Store the answer directly on the sender's node
          const answerData = {
            from: clientId,
            sdp: answer,
            time: Date.now(),
          };

          debugLog(`Storing answer: ${JSON.stringify(answerData)}`, 'rtc');

          // Use put with a callback to ensure the answer is stored
          room
            .get('answer')
            .get(senderId)
            .put(answerData, (ack) => {
              if (ack.err) {
                log(`Error storing answer: ${ack.err}`, 'error');
              } else {
                log(`Stored answer for ${senderId} in GunDB`, 'gun');
              }
            });
        } catch (error) {
          log(`Error creating answer: ${error.message}`, 'error');
          console.error('Full error:', error);
          updateConnectionState(CONNECTION_STATES.FAILED);
        }
      }

      // Handle incoming WebRTC answer
      async function handleIncomingAnswer(answerData) {
        const senderId = answerData.from;

        log(`Processing answer from ${senderId}`, 'rtc');

        // Get existing RTC connection
        const rtc = rtcConnections[senderId];
        if (!rtc) {
          log(`Received answer from ${senderId} but no RTC connection exists`, 'error');
          return;
        }

        try {
          // Set the answer to complete the connection - this handles adding ICE candidates internally
          log(`Setting answer from ${senderId}...`, 'rtc');
          await rtc.setAnswer(answerData.sdp);
          log(`Set answer from ${senderId}, connection should establish`, 'rtc');
        } catch (error) {
          log(`Error setting answer: ${error.message}`, 'error');
          console.error('Full error:', error);
          updateConnectionState(CONNECTION_STATES.FAILED);
        }
      }

      // Create a room
      function createRoom() {
        try {
          // Generate a room ID
          const roomId = generateRoomId();
          log(`Creating room: ${roomId}`, 'gun');

          // Connect to the room
          if (connectToRoom(roomId)) {
            // Update UI
            updateConnectionState(CONNECTION_STATES.WAITING);

            // Generate and store share link (hidden)
            const shareUrl = window.location.origin + window.location.pathname + '#r=' + roomId;
            shareUrlInput.value = shareUrl;
            shareUrlContainer.style.display = 'block';

            // Set URL hash
            window.location.hash = `#r=${roomId}`;

            // Copy link to clipboard
            navigator.clipboard
              .writeText(shareUrl)
              .then(() => {
                createRoomBtn.textContent = 'Copied Link';
                log('Share link copied to clipboard', 'system');
                log(`Your client ID: ${clientId}`, 'system');
              })
              .catch((err) => {
                log(`Failed to copy link: ${err}`, 'error');
                createRoomBtn.textContent = 'Copy Room URL';
              });

            log('Ready for WebRTC connections', 'gun');
          }
        } catch (error) {
          log(`Error creating room: ${error.message}`, 'error');
          updateConnectionState(CONNECTION_STATES.FAILED);
        }
      }

      // Join a room from URL hash
      function joinRoom(roomId) {
        try {
          log(`Joining room: ${roomId}`, 'gun');
          log(`Your client ID: ${clientId}`, 'system');

          // Connect to the room
          if (connectToRoom(roomId)) {
            // Update UI
            updateConnectionState(CONNECTION_STATES.WAITING);

            // Store share link (hidden)
            const shareUrl = window.location.origin + window.location.pathname + '#r=' + roomId;
            shareUrlInput.value = shareUrl;
            shareUrlContainer.style.display = 'block';
            createRoomBtn.textContent = 'Copy Room URL';

            log('Ready for WebRTC connections', 'gun');
          }
        } catch (error) {
          log(`Error joining room: ${error.message}`, 'error');
          updateConnectionState(CONNECTION_STATES.FAILED);
        }
      }

      // Send a test message to all connected peers
      function sendTestMessage() {
        const message = testMessageInput.value.trim();
        if (!message) return;

        let sentToAny = false;

        // Send to all connected peers
        for (const peerId in rtcConnections) {
          const rtc = rtcConnections[peerId];
          if (rtc.isConnected()) {
            rtc.sendMessage(message);
            sentToAny = true;
          }
        }

        if (sentToAny) {
          // Add message to local display
          addChatMessage(message, 'self');

          // Clear input
          testMessageInput.value = '';
        } else {
          alert('No connected peers to send to!');
        }
      }

      // Check URL hash for room ID on load
      function checkHashAndJoinRoom() {
        if (window.location.hash.startsWith('#r=')) {
          const roomId = window.location.hash.substring(3);
          if (roomId) {
            joinRoom(roomId);
          }
        } else {
          updateConnectionState(CONNECTION_STATES.WAITING);
        }
      }

      // Event listeners
      createRoomBtn.addEventListener('click', () => {
        if (currentRoomId) {
          // If already in a room, copy URL when clicked
          navigator.clipboard
            .writeText(shareUrlInput.value)
            .then(() => {
              createRoomBtn.textContent = 'Copied Link';
              setTimeout(() => {
                createRoomBtn.textContent = 'Copy Room URL';
              }, 2000);
            })
            .catch((err) => log(`Copy error: ${err}`, 'error'));
        } else {
          // If not in a room, create one
          createRoom();
        }
      });

      sendTestBtn.addEventListener('click', sendTestMessage);

      testMessageInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          sendTestMessage();
        }
      });

      // Initialize
      window.addEventListener('load', () => {
        checkHashAndJoinRoom();
        log('Ready to create or join a room', 'system');
      });

      window.addEventListener('hashchange', () => {
        checkHashAndJoinRoom();
      });
    